/**
 * AttachCustomerFileModal(REQ-20260518002 + REQ-20260519003):
 * 以「訪談」為主軸列出已綁定客戶的訪談紀錄,內含該筆訪談的活附件。
 * - 點既有附件 → 走 LINE 圖片訊息(jpg/png ≤ 1 MB)或文字 + 24h 下載連結
 * - 每筆訪談下方提供「+ 上傳新檔案並送出」按鈕,
 *   走既有 presigned upload → 寫入 InterviewAttachment → 透過 LINE 送出
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LineCustomerAttachmentItem, LineCustomerInterview } from '@app/shared';
import { fetchDownloadUrl, uploadAttachment } from '../attachments/api';
import { Modal } from '../../components/Modal';
import { useCustomerInterviews, useSendAttachment } from './lineApi';

interface Props {
  open: boolean;
  contactId: number;
  onClose: () => void;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtMime(mime: string): string {
  return (mime.split(';')[0] ?? '').trim().replace(/^application\//, '');
}

function isImageMime(mime: string): boolean {
  return /^image\/(jpeg|png|gif|webp)/i.test(mime);
}

function fileIconOf(mime: string): string {
  const m = mime.toLowerCase();
  if (m === 'application/pdf') return '📕';
  if (m.includes('word') || m.includes('msword')) return '📄';
  if (m.includes('sheet') || m.includes('excel')) return '📊';
  if (m.includes('presentation') || m.includes('powerpoint')) return '📊';
  if (m.startsWith('video/')) return '🎬';
  if (m.startsWith('audio/')) return '🎵';
  if (m === 'application/zip' || m === 'application/x-zip-compressed') return '🗜️';
  if (m === 'text/csv' || m === 'text/plain') return '📋';
  return '📎';
}

/**
 * 縮圖預覽:
 * - 圖片 mime → axios 拿 InterviewAttachment 的 presigned URL → <img>
 *   (沿用 /attachments/:uuid/download 模式;<img> 不會帶 JWT,所以必須先 fetch)
 * - 其他類型 → 顯示對應 emoji 圖示
 * 元件本身固定 56×56,讓清單對齊。
 */
function AttachmentThumb({
  uuid,
  mimeType,
  name,
}: {
  uuid: string;
  mimeType: string;
  name: string;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const isImage = isImageMime(mimeType);

  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    setUrl(null);
    setError(false);
    fetchDownloadUrl(uuid)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, isImage]);

  if (!isImage) {
    return (
      <div
        className="w-14 h-14 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-2xl shrink-0"
        title={mimeType}
      >
        {fileIconOf(mimeType)}
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-14 h-14 rounded bg-red-50 border border-red-200 flex items-center justify-center text-xs text-red-500 shrink-0">
        ✗
      </div>
    );
  }
  if (!url) {
    return (
      <div className="w-14 h-14 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-xs text-slate-400 shrink-0 animate-pulse">
        ⋯
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="w-14 h-14 shrink-0 block border border-slate-200 rounded overflow-hidden bg-slate-50"
      title={`點開預覽原圖:${name}`}
    >
      <img
        src={url}
        alt={name}
        loading="lazy"
        className="w-full h-full object-cover"
        referrerPolicy="no-referrer"
      />
    </a>
  );
}

export function AttachCustomerFileModal({ open, contactId, onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useCustomerInterviews(contactId, open);
  const send = useSendAttachment();
  const [busyInterview, setBusyInterview] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onPickExisting = (a: LineCustomerAttachmentItem): void => {
    if (send.isPending) return;
    const hint = a.canSendAsImage
      ? `要把「${a.originalName}」以 LINE 圖片訊息送給客戶嗎?`
      : `要把「${a.originalName}」以下載連結文字訊息送給客戶嗎?(連結 24 小時內有效)`;
    if (!window.confirm(hint)) return;
    send.mutate(
      { contactId, attachmentUuid: a.uuid },
      { onSuccess: () => onClose() },
    );
  };

  const onUploadAndSend = async (interviewId: number, file: File): Promise<void> => {
    setUploadError(null);
    setBusyInterview(interviewId);
    try {
      // 1. 上傳到 MinIO + 寫入 InterviewAttachment(重用既有 3-step flow)
      const att = await uploadAttachment(interviewId, file);
      // 2. 用既有 line attachment send endpoint 送出
      await send.mutateAsync({ contactId, attachmentUuid: att.uuid });
      // 3. 重整訪談清單(讓新附件出現在原訪談下)
      await Promise.all([
        refetch(),
        qc.invalidateQueries({ queryKey: ['interviews', 'detail'] }),
        qc.invalidateQueries({ queryKey: ['interview-attachments', interviewId] }),
      ]);
      onClose();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : '未知錯誤');
    } finally {
      setBusyInterview(null);
    }
  };

  const items = data?.items ?? [];
  const customerName = data ? `${data.customerSimpleName}${data.customerCCode ? ` (#${data.customerCCode})` : ''}` : '';

  return (
    <Modal open={open} onClose={onClose} title={customerName ? `附加客戶檔案 — ${customerName}` : '附加客戶檔案'} widthClass="max-w-3xl" dismissOnBackdrop={busyInterview == null}>
      <div className="grid gap-3">
        <p className="text-xs text-slate-500">
          列出已綁定客戶的訪談紀錄(由新到舊),每筆訪談內含該訪談的活附件。
          可點既有附件直接送出;尚無附件的訪談下方可「上傳新檔案並送出」,
          新檔會同時記到該筆訪談的附件清單。
          圖片(jpg / png ≤ 1 MB)走 LINE 原生圖片訊息,其他類型送「檔名 + 24 小時連結」文字訊息。
        </p>

        <div className="border rounded max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-sm text-slate-500">載入中…</div>
          ) : isError ? (
            <div className="p-3 text-sm text-red-600">
              載入失敗:{error instanceof Error ? error.message : '未知錯誤'}
            </div>
          ) : items.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">此客戶尚無訪談紀錄</div>
          ) : (
            items.map((iv) => (
              <InterviewBlock
                key={iv.interviewId}
                iv={iv}
                busyHere={busyInterview === iv.interviewId}
                anyBusy={busyInterview != null || send.isPending}
                onPickExisting={onPickExisting}
                onUpload={(f) => void onUploadAndSend(iv.interviewId, f)}
              />
            ))
          )}
        </div>

        {send.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            送出失敗:{send.error instanceof Error ? send.error.message : '未知錯誤'}
          </div>
        ) : null}
        {uploadError ? (
          <div role="alert" className="text-red-600 text-sm">
            上傳失敗:{uploadError}
          </div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busyInterview != null}
            className="text-sm border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            關閉
          </button>
        </div>
      </div>
    </Modal>
  );
}

function InterviewBlock({
  iv,
  busyHere,
  anyBusy,
  onPickExisting,
  onUpload,
}: {
  iv: LineCustomerInterview;
  busyHere: boolean;
  anyBusy: boolean;
  onPickExisting: (a: LineCustomerAttachmentItem) => void;
  onUpload: (f: File) => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const date = new Date(iv.interviewCreatedAt).toLocaleDateString('zh-TW');
  return (
    <section className="border-b last:border-b-0">
      <header className="px-3 py-2 bg-slate-50 text-xs text-slate-600 flex items-center gap-2">
        <span className="font-medium text-slate-800">{date}</span>
        {iv.interviewTypeName ? <span>· {iv.interviewTypeName}</span> : null}
        {iv.contact ? <span>· 聯絡人:{iv.contact}</span> : null}
        <span className="ml-auto text-slate-500">
          附件 {iv.attachments.length} 件
        </span>
      </header>
      {iv.notesPreview ? (
        <div className="px-3 py-1.5 text-xs text-slate-600 border-b bg-white">
          {iv.notesPreview}
        </div>
      ) : null}

      {iv.attachments.length > 0 ? (
        <ul className="divide-y">
          {iv.attachments.map((a) => (
            <li
              key={a.uuid}
              className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50"
            >
              <AttachmentThumb uuid={a.uuid} mimeType={a.mimeType} name={a.originalName} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{a.originalName}</div>
                <div className="text-xs text-slate-500">
                  {fmtMime(a.mimeType)} · {fmtSize(a.sizeBytes)}
                </div>
              </div>
              <button
                type="button"
                disabled={anyBusy}
                onClick={() => onPickExisting(a)}
                className={
                  'text-xs rounded px-2 py-1 border disabled:opacity-50 shrink-0 ' +
                  (a.canSendAsImage
                    ? 'border-blue-600 text-blue-700 hover:bg-blue-50'
                    : 'border-slate-400 text-slate-700 hover:bg-slate-50')
                }
              >
                {a.canSendAsImage ? '送圖片' : '送連結'}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {iv.attachments.length === 0 ? (
        <div className="px-3 py-2 bg-white border-t flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">此訪談尚無附件</span>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <button
              type="button"
              disabled={anyBusy}
              onClick={() => fileInputRef.current?.click()}
              className="text-xs border border-emerald-600 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-50 disabled:opacity-50"
            >
              {busyHere ? '上傳中…' : '+ 上傳新檔案並送出'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
